/**
 * The unauthenticated login-method discovery contract: `POST /auth/methods`,
 * `{ email }` → `{ methods, next }`.
 *
 * This file is the browser-safe half of the feature (`@pima/identity/http`):
 * it imports nothing but Effect, so the web client can build a typed client
 * from it without pulling the identity layer's database adapter into a
 * bundle. The group is defined here and mounted by `packages/web-api`, which
 * adds it to the `/web/v1` contract and serves it with `discoverLoginMethods`.
 */
import { HttpApi, HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import { Schema as S } from "effect";
/** The single-sign-on providers the login screen can start. */
export const SSO_PROVIDERS = ["google", "microsoft", "okta"];
export const LoginMethod = S.Literal("password", ...SSO_PROVIDERS);
/**
 * Which step the login screen continues with.
 *
 * `password` and `sso` are the two a usable address can receive; `unknown` is
 * the answer for input that is not an address at all. `signup` is part of the
 * agreed contract but no domain policy produces it yet.
 *
 * `sso` comes with the company's providers in `methods`. `password` comes
 * with every method in `methods`: the password step opens, and the screen
 * offers the providers beside it.
 *
 * None of them says whether the address is registered: the answer is a pure
 * function of the address's domain, so every address in a domain receives the
 * same one.
 */
export const LoginContinuation = S.Literal("password", "sso", "signup", "unknown");
export const LoginMethodsPayload = S.Struct({
    // Bounded so an anonymous caller cannot hand the server an arbitrarily large
    // string to normalize; 320 is the longest address SMTP can carry.
    email: S.String.pipe(S.maxLength(320)),
});
export const LoginMethodsResponse = S.Struct({
    methods: S.Array(LoginMethod),
    next: LoginContinuation,
});
/**
 * Public by design: the group carries no principal middleware, because it is
 * what a person who is not signed in yet asks first. It is safe to serve
 * anonymously only because the answer is keyed by the domain.
 */
export const authGroup = HttpApiGroup.make("auth").add(HttpApiEndpoint.post("methods", "/auth/methods")
    .setPayload(LoginMethodsPayload)
    .addSuccess(LoginMethodsResponse));
/**
 * The group on its own, for a slim server or client that should carry
 * nothing else. Production serves the group as part of the web-api contract.
 */
export const authApi = HttpApi.empty.add(authGroup);
//# sourceMappingURL=login-methods.http.js.map